import { AnnotationViewerVariants } from "@/components/ui/annotations/annotation-viewer/annotation-viewer-types";
import { convertIElementToThreePositionParsed } from "@/utils/transform-conversion-parsed";
import { GUID } from "@faro-lotv/foundation";
import { PolygonPoint } from "@faro-lotv/ielement-types";
import { useMemo, type JSX } from "react";
import { Matrix4 } from "three";
import { AnnotationShapeMesh } from "./annotation-shape-mesh";
import {
  DEFAULT_SHAPE_STYLE,
  MIN_POLYGON_POINTS,
  useAnnotationShapeStyle,
  useIsShapeBeingEdited,
} from "./annotation-shapes-store";

type AnnotationShapeRendererProps = {
  markupId: GUID;

  /** Corners of the shape, in the local coordinates of the annotation */
  points: PolygonPoint[];

  /** Transform from the annotation local coordinates to world */
  transform: Matrix4;

  /** Current state of the annotation pin and card */
  variant: AnnotationViewerVariants;
};

/**
 * @returns the area of a rectangle or polygon annotation. Like area comments in Figma, the annotation is a pin,
 * and its area is only revealed for the pin under the cursor (a light preview) or for the open annotation.
 * So areas never pile up on each other, at any zoom, even when they overlap.
 */
export function AnnotationShapeRenderer({
  markupId,
  points,
  transform,
  variant,
}: AnnotationShapeRendererProps): JSX.Element | null {
  const style = useAnnotationShapeStyle(markupId) ?? DEFAULT_SHAPE_STYLE;
  // While its style is edited the shape stays shown, to preview the changes
  const isEdited = useIsShapeBeingEdited(markupId);

  const worldPoints = useMemo(
    () =>
      points.map((p) =>
        convertIElementToThreePositionParsed(p).applyMatrix4(transform),
      ),
    [points, transform],
  );

  const isOpen = variant === "full" || isEdited;
  if (
    worldPoints.length < MIN_POLYGON_POINTS ||
    (!isOpen && variant !== "small")
  ) {
    return null;
  }

  return (
    <AnnotationShapeMesh
      points={worldPoints}
      style={style}
      emphasis={isOpen ? "active" : "preview"}
    />
  );
}
